import {
  BackLink,
  Button,
  Headline,
  Spinner,
  Stack,
  Tag,
} from '@servicetitan/design-system';
import styles from './CustomerDetails.module.css';
import CustomerLocations from './components/CustomerLocations';
import { CustomerInformationV2 } from './components/CustomerInformation';
import ErrorComponent from '@components/ErrorComponent/ErrorComponent';
import {
  strings as errorStrings,
  strings,
} from '@components/ErrorComponent/strings';
import { inboxLocators } from '@locators/inbox';
import extractErrorMessage from '@lib/utils/extractErrorMessage';
import CustomerContactMethods from './components/CustomerContactMethods/CustomerContactMethods';
import LinkoutButton from '@components/LinkoutButton/LinkoutButton';
import { LocationsDrawer } from '../LocationsDrawer/LocationsDrawer';
import { useMemo, useState } from 'react';
import { EditContactsDrawer } from '../ContactsDrawers/EditContactsDrawer';
import ViewAllContactsDrawer from '../ContactsDrawers/ViewAllContactsDrawer/ViewAllContactsDrawer';
import useGetCustomerById from '@api/queries/getCustomerById/useGetCustomerById';
import useLazyGetCustomerContacts from '@api/queries/getCustomerById/getCustomerContacts';
import CustomerContext from '@components/CustomerContext/CustomerContext';
import {
  bookJobLinkToMonolith,
  customerLinkToMonolith,
  locationLinkToMonolith,
} from '@lib/utils/tenantLinks';
import AuthModule from '@modules/AuthModule';
import { FeatureErrorBoundary } from '@components/ErrorBoundary';

interface CustomerDetailsProps {
  customerId: string;
  tenantId: string;
  displayName?: string;
  onBackClick: () => void;
}

const CustomerDetails = ({
  customerId,
  tenantId,
  displayName,
  onBackClick,
}: CustomerDetailsProps) => {
  const [locationDrawerOpen, setLocationDrawerOpen] = useState(false);
  const [editCustomerOpened, setEditCustomerOpened] = useState(false);
  const [viewAllContactsDrawerOpen, setViewAllContactsDrawerOpen] =
    useState(false);

  const { customer, loading, error, refetch } = useGetCustomerById(
    tenantId,
    customerId,
    false,
  );

  const {
    fetchCustomerContacts: refetchContacts,
    loading: customerContactsLoading,
    error: customerContactsError,
  } = useLazyGetCustomerContacts(tenantId, customerId);

  const handleCustomerLink = () => {
    window.open(customerLinkToMonolith(tenantId, customer?.id ?? ''), '_blank');
    AuthModule.resetAuthAge();
  };

  const handleBookJobLink = () => {
    window.open(bookJobLinkToMonolith(tenantId), '_blank');
    AuthModule.resetAuthAge();
  };

  const handleLocationLink = (locationId: string) => {
    window.open(locationLinkToMonolith(tenantId, locationId), '_blank');
    AuthModule.resetAuthAge();
  };

  const handleRefetchCustomerContacts = async () => {
    return refetchContacts();
  };

  const customerContextValue = useMemo(
    () => ({
      customerName: customer?.name ?? '',
      tenantId,
      customerId: customer?.id ?? '',
    }),
    [customer, tenantId],
  );

  return (
    <div className='w-100'>
      <CustomerContext.Provider value={customerContextValue}>
        <Stack direction='column' spacing={3}>
          <div>
            <BackLink
              className='m-b-2'
              pageLabel='Search'
              onClick={onBackClick}
            />
            <Stack direction='column' spacing={1}>
              <Stack
                className='m-b-1'
                direction='row'
                justifyContent='space-between'
                alignItems='center'
              >
                <Stack direction='row' spacing={2} alignItems='center'>
                  <Headline className='m-0' size='large'>
                    {customer?.name ?? displayName ?? ''}
                  </Headline>
                  {customer && (
                    <LinkoutButton
                      id={
                        inboxLocators.customerSearch.customerLink +
                        tenantId +
                        '-' +
                        `${customer?.id ?? ''}`
                      }
                      tooltipText='Customer Link'
                      onClick={handleCustomerLink}
                    />
                  )}
                </Stack>
                <Stack spacing={2}>
                  {customer && (
                    <Button
                      color='primary'
                      size='small'
                      onClick={handleBookJobLink}
                    >
                      Book Job
                    </Button>
                  )}
                  <Button
                    data-testid='refresh-customer-button'
                    id={inboxLocators.customerSearch.customerRefreshButton}
                    fill='subtle'
                    size='small'
                    iconName='refresh'
                    onClick={() => refetch()}
                    aria-label='Refresh Customer'
                  />
                </Stack>
              </Stack>
              {customer && (
                <Stack spacing={1}>
                  <Stack.Item>
                    {!!customer?.doNotService && (
                      <Tag
                        className='m-t-1 m-r-1'
                        color='inactive'
                        id={inboxLocators.customerSearch.customerTag}
                        subtle={true}
                      >
                        Do Not Service
                      </Tag>
                    )}

                    {customer?.tags?.map((tag) => (
                      <Tag
                        key={tag.name}
                        className='m-r-1 m-t-1'
                        color={tag.color}
                        id={inboxLocators.customerSearch.customerTag}
                      >
                        {tag.name}
                      </Tag>
                    ))}
                  </Stack.Item>
                </Stack>
              )}
            </Stack>
          </div>
          {customer?.locations && (
            <FeatureErrorBoundary
              featureName='Customer Locations'
              size='compact'
            >
              <CustomerLocations
                locations={customer.locations}
                onViewAllClicked={() => setLocationDrawerOpen(true)}
                onLinkOutClicked={handleLocationLink}
              />
            </FeatureErrorBoundary>
          )}
          {customer && (
            <div className='d-f gap-3 flex-wrap'>
              <FeatureErrorBoundary
                featureName='Customer Information'
                size='compact'
              >
                <CustomerInformationV2
                  tenantId={tenantId}
                  customerId={customerId}
                />
              </FeatureErrorBoundary>

              <FeatureErrorBoundary
                featureName='Customer Contact Methods'
                size='compact'
              >
                <CustomerContactMethods
                  contacts={customer.contacts ?? []}
                  contactsLoading={customerContactsLoading}
                  onEditClicked={() => setEditCustomerOpened(true)}
                  contactsError={customerContactsError}
                  onViewAllClicked={() => setViewAllContactsDrawerOpen(true)}
                />
              </FeatureErrorBoundary>
            </div>
          )}
        </Stack>
        {loading && (
          <div className={styles.noDataContainer}>
            <Spinner
              id={inboxLocators.customerSearch.customerLoadingSpinner}
              size='large'
            />
          </div>
        )}
        {!loading && (!customer || error) && (
          <div className={styles.noDataContainer}>
            <ErrorComponent
              title={strings.errorHeadline}
              body={`${strings.defaultError} (Error: ${extractErrorMessage(error)})`}
              primaryAction={refetch}
              primaryActionLabel={errorStrings.labels.retry}
            />
          </div>
        )}
        {locationDrawerOpen && (
          <FeatureErrorBoundary featureName='Locations Drawer'>
            <LocationsDrawer
              customerId={customerId}
              tenantId={tenantId}
              open={locationDrawerOpen}
              onClose={() => setLocationDrawerOpen(false)}
            />
          </FeatureErrorBoundary>
        )}
        <FeatureErrorBoundary featureName='Edit Contacts Drawer'>
          <EditContactsDrawer
            tenantId={tenantId}
            customerId={customerId}
            open={editCustomerOpened}
            onClose={() => setEditCustomerOpened(false)}
            contacts={customer?.contacts || []}
            refetchCustomerContacts={handleRefetchCustomerContacts}
          />
        </FeatureErrorBoundary>
        <FeatureErrorBoundary featureName='View All Contacts Drawer'>
          <ViewAllContactsDrawer
            contacts={customer?.contacts || []}
            customerName={customer?.name || ''}
            customerContactsLoading={customerContactsLoading}
            customerContactsError={!!customerContactsError}
            customerId={customerId}
            tenantId={tenantId}
            open={viewAllContactsDrawerOpen}
            onClose={() => setViewAllContactsDrawerOpen(false)}
            refetchCustomerContacts={handleRefetchCustomerContacts}
          />
        </FeatureErrorBoundary>
      </CustomerContext.Provider>
    </div>
  );
};

export default CustomerDetails;
